import type {
  BulkSetCourseDefinitionActiveRequest,
  CourseDefinitionRequest,
  CourseDefinitionResponse,
  CourseQuizStatisticsResponse,
  GetCourseDefinitionContentListRequest,
  GetCourseDefinitionListRequest,
  GetCourseDefinitionListRequestV2,
  GetListResponseOfCourseDefinitionSimpleResponse,
  GetListResponseOfSelectOption,
  GetListResponseOfSelectOptionInvariant,
} from 'src/api/ProactApiClient';
import {courseDefinitionApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

export const courseDefinitionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCourseDefinitionSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetCourseDefinitionListRequest
    >({
      queryFn: clientQuery(params =>
        courseDefinitionApiClient.listSelectOptions(params)
      ),
      providesTags: ['CourseDefinitions'],
    }),
    getCourseDefinitionContentSelectOptions: builder.query<
      GetListResponseOfSelectOptionInvariant,
      GetCourseDefinitionContentListRequest
    >({
      queryFn: clientQuery(params =>
        courseDefinitionApiClient.listContentSelectOptions(params)
      ),
      providesTags: ['CourseDefinitions'],
    }),
    getCourseDefinitionList: builder.query<
      GetListResponseOfCourseDefinitionSimpleResponse,
      GetCourseDefinitionListRequest
    >({
      queryFn: clientQuery(params => courseDefinitionApiClient.list(params)),
      providesTags: ['CourseDefinitions'],
    }),
    getCourseDefinitionListV2: builder.query<
      GetListResponseOfCourseDefinitionSimpleResponse,
      GetCourseDefinitionListRequestV2
    >({
      queryFn: clientQuery(params => courseDefinitionApiClient.listV2(params)),
      providesTags: ['CourseDefinitions'],
    }),
    getCourseDefinition: builder.query<CourseDefinitionResponse, number>({
      queryFn: clientQuery(id => courseDefinitionApiClient.get(id)),
      providesTags: ['CourseDefinitions'],
    }),
    getCourseDefinitionQuizStatistics: builder.query<
      CourseQuizStatisticsResponse,
      {id: number; language?: string; fromUtc?: string; toUtc?: string}
    >({
      queryFn: clientQuery(({id, language, fromUtc, toUtc}) =>
        courseDefinitionApiClient.getQuizStatistics(
          id,
          language,
          fromUtc ? new Date(fromUtc) : undefined,
          toUtc ? new Date(toUtc) : undefined
        )
      ),
      providesTags: ['CourseDefinitions'],
    }),
    createCourseDefinition: builder.mutation<
      CourseDefinitionResponse,
      CourseDefinitionRequest
    >({
      queryFn: clientQuery(model => courseDefinitionApiClient.create(model)),
      invalidatesTags: ['CourseDefinitions'],
    }),
    updateCourseDefinition: builder.mutation<
      CourseDefinitionResponse,
      CourseDefinitionRequest
    >({
      queryFn: clientQuery(model => courseDefinitionApiClient.update(model)),
      invalidatesTags: ['CourseDefinitions'],
    }),
    cloneCourseDefinition: builder.mutation<CourseDefinitionResponse, number>({
      queryFn: clientQuery(id => courseDefinitionApiClient.clone(id)),
      invalidatesTags: ['CourseDefinitions'],
    }),
    bulkSetCourseDefinitionActive: builder.mutation<
      number,
      BulkSetCourseDefinitionActiveRequest
    >({
      queryFn: clientQuery(request =>
        courseDefinitionApiClient.bulkSetActive(request)
      ),
      invalidatesTags: ['CourseDefinitions'],
    }),
  }),
});

export const {
  useGetCourseDefinitionListQuery,
  useGetCourseDefinitionContentSelectOptionsQuery,
  useGetCourseDefinitionSelectOptionsQuery,
  useGetCourseDefinitionListV2Query,
  useGetCourseDefinitionQuery,
  useGetCourseDefinitionQuizStatisticsQuery,
  useCreateCourseDefinitionMutation,
  useUpdateCourseDefinitionMutation,
  useCloneCourseDefinitionMutation,
  useBulkSetCourseDefinitionActiveMutation,
} = courseDefinitionApi;
